import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isInstalledApp } from '@/lib/appUrl';
import { Button } from '@/components/ui/button';
import React, { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowLeft, Bell, Apple, Smartphone, Share, MoreVertical, Plus } from 'lucide-react';

const Step = ({ n, title, body, icon: Icon }) => (
  <div className="flex items-start gap-3 py-3">
    <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0">
      {n}
    </div>
    <div className="flex-1">
      <p className="font-semibold flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4" /> : null}
        {title}
      </p>
      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);

const InstallPwaPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const home = isAuthenticated ? "/dashboard" : "/";
  const defaultTab = useMemo(() => {
    if (typeof navigator === 'undefined') return 'ios';
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) return 'ios';
    return 'android';
  }, []);

  if (isInstalledApp()) return <Navigate to={home} replace />;

  return (
    <div className="safe-public-page">
    <div className="max-w-2xl mx-auto px-4 py-6 md:py-10">
      <Button variant="ghost" className="mb-4" onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate(home, { replace: true })}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Indietro
      </Button>
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
          <Bell className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Installa LeagueHub come app</h1>
          <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
            Installando LeagueHub sul telefono ricevi le notifiche push (reminder
            partita, promozioni da riserva, nuove iscrizioni) anche quando il
            browser è chiuso. Nessun App Store, nessun aggiornamento manuale.
          </p>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="ios" data-testid="pwa-tab-ios">
            <Apple className="h-4 w-4 mr-2" />
            iPhone / iPad
          </TabsTrigger>
          <TabsTrigger value="android" data-testid="pwa-tab-android">
            <Smartphone className="h-4 w-4 mr-2" />
            Android
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ios" className="mt-4">
          <Card className="p-4 md:p-6 divide-y">
            <Step
              n={1}
              title="Apri leaguehub.it in Safari"
              body="L'installazione funziona solo da Safari. Se stai leggendo questa pagina dentro un'altra app (Instagram, Gmail, WhatsApp), tocca i tre puntini in alto e scegli 'Apri in Safari'."
            />
            <Step
              n={2}
              title='Tocca il tasto "Condividi"'
              icon={Share}
              body="È l'icona con il quadrato e la freccia verso l'alto. Su iPhone la trovi nella barra in basso al centro; su iPad in alto a destra."
            />
            <Step
              n={3}
              title='Scegli "Aggiungi alla schermata Home"'
              icon={Plus}
              body="Scorri il menu di condivisione finché trovi questa voce. Tocca 'Aggiungi' in alto a destra per confermare."
            />
            <Step
              n={4}
              title="Apri LeagueHub dall'icona sulla home"
              icon={Bell}
              body="Da iOS 16.4 in poi, dopo aver aperto l'app dalla home ti chiederemo il permesso per le notifiche push. Conferma per ricevere i reminder partita."
            />
          </Card>
        </TabsContent>

        <TabsContent value="android" className="mt-4">
          <Card className="p-4 md:p-6 divide-y">
            <Step
              n={1}
              title="Apri leaguehub.it in Chrome"
              body="Funziona meglio con Chrome. Se sei in un'altra app (WhatsApp, Instagram), tocca i tre puntini e scegli 'Apri in Chrome'."
            />
            <Step
              n={2}
              title="Tocca i tre puntini ⋮ in alto a destra"
              icon={MoreVertical}
              body="Si apre il menu di Chrome. In alcune versioni vedrai direttamente un banner 'Installa app' sotto la barra dell'indirizzo: in quel caso basta un tocco."
            />
            <Step
              n={3}
              title='Scegli "Installa app" o "Aggiungi alla schermata Home"'
              icon={Plus}
              body="Il testo cambia tra le versioni di Chrome. Conferma il nome 'LeagueHub' e tocca 'Installa'."
            />
            <Step
              n={4}
              title="Autorizza le notifiche al primo avvio"
              icon={Bell}
              body="Aperta dall'icona, l'app ti chiederà il permesso per le notifiche. Conferma per ricevere reminder partita e aggiornamenti iscrizioni."
            />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </div>
  );
};

export default InstallPwaPage;
